import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainTemplate from "../../components/MainTemplate/MainTemplate";
import CardBackground from "../../components/MainTemplate/CardBackground";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import axios from "axios";

const SponsorshipApplication = () => {
  const { state } = useLocation();
  const edetails = state;
  return (
    <MainTemplate>
      <CardBackground>
        <SponsorshipApplicationChild edetails={edetails} />
      </CardBackground>
    </MainTemplate>
  );
};

const SponsorshipApplicationChild = ({ edetails }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    sponsor_name: "",
    sponsor_type: { value: "regular", label: "Regular" },
    sponsor_amount: "",
    sponsor_description: "",
    sponsor_contact: {
      name: "",
      email: "",
      phone: "",
    },
  });
  const [sponsorLogo, setSponsorLogo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please upload an image file");
        return;
      }
      // Validate file size (e.g., 5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError("File size should be less than 5MB");
        return;
      }
      setSponsorLogo(file);
      setError("");
    }
  };

  const validateForm = () => {
    if (!formData.sponsor_name.trim()) {
      setError("Sponsor name is required");
      return false;
    }
    if (!formData.sponsor_amount || formData.sponsor_amount <= 0) {
      setError("Valid sponsor amount is required");
      return false;
    }
    if (!formData.sponsor_contact.name.trim()) {
      setError("Contact name is required");
      return false;
    }
    if (!formData.sponsor_contact.email.trim()) {
      setError("Contact email is required");
      return false;
    }
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.sponsor_contact.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    setError("");

    try {
      const formDataToSend = new FormData();

      // Append all form fields
      formDataToSend.append("event_id", edetails._id);
      formDataToSend.append("sponsor_name", formData.sponsor_name);
      formDataToSend.append(
        "sponsor_type",
        JSON.stringify(formData.sponsor_type)
      );
      formDataToSend.append("sponsor_amount", formData.sponsor_amount);
      formDataToSend.append(
        "sponsor_description",
        formData.sponsor_description
      );

      // Append contact fields individually
      formDataToSend.append(
        "sponsor_contact[name]",
        formData.sponsor_contact.name
      );
      formDataToSend.append(
        "sponsor_contact[email]",
        formData.sponsor_contact.email
      );
      formDataToSend.append(
        "sponsor_contact[phone]",
        formData.sponsor_contact.phone || ""
      );

      // Append the file if it exists
      if (sponsorLogo) {
        formDataToSend.append("sponsor_logo", sponsorLogo);
      }

      // Add church_id from edetails
      formDataToSend.append("church_id", edetails.church_id._id);

      let userToken = localStorage.getItem("token");
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/sponsor/add/`,
        data: formDataToSend,
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      // Show success message and redirect
      navigate("/events-calender", {
        state: {
          message:
            response.data.message ||
            "Sponsorship application submitted successfully!",
        },
      });
    } catch (err) {
      console.error("Submission error:", err);

      if (axios.isAxiosError(err)) {
        // Handle Axios specific errors
        if (!err.response) {
          setError(
            "Unable to connect to the server. Please check your internet connection and try again."
          );
        } else {
          // Get error message from response
          const errorMessage =
            err.response.data?.error ||
            err.response.data?.message ||
            "Failed to submit sponsorship application";
          setError(errorMessage);
        }
      } else {
        // Handle non-Axios errors
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-6xl p-6 mx-auto">
      {/* Event Summary Card */}
      <div className="mb-8">
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Sponsorship Application
              </h2>
              <p className="mt-1 text-gray-600">
                <span className="font-medium">{edetails.title}</span> |{" "}
                {edetails.view_event_start_date}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsEventDetailsOpen(!isEventDetailsOpen)}
              className="p-2 text-gray-500 rounded-full hover:bg-gray-100"
            >
              {isEventDetailsOpen ? (
                <ChevronUpIcon className="w-5 h-5" />
              ) : (
                <ChevronDownIcon className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Collapsible Event Details */}
          {isEventDetailsOpen && (
            <div className="pt-4 mt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Event Type
                  </h4>
                  <p className="mt-1">{edetails.event_type.label}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Location
                  </h4>
                  <p className="mt-1">{edetails.event_location}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">
                    Date & Time
                  </h4>
                  <p className="mt-1">
                    {edetails.view_event_start_date} to{" "}
                    {edetails.view_event_end_date}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Contact</h4>
                  <p className="mt-1">{edetails.event_contact_number}</p>
                </div>
                <div className="col-span-2">
                  <h4 className="text-sm font-medium text-gray-500">
                    Description
                  </h4>
                  <p className="mt-1 text-gray-600">{edetails.description}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sponsor Information - Now wider */}
        <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h3 className="mb-6 text-xl font-semibold text-gray-700">
            Sponsor Information
          </h3>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="lg:col-span-1">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Sponsor Name *
              </label>
              <input
                type="text"
                name="sponsor_name"
                value={formData.sponsor_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Sponsorship Amount (USD) *
              </label>
              <input
                type="number"
                name="sponsor_amount"
                value={formData.sponsor_amount}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="lg:col-span-2">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Sponsor Logo
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {sponsorLogo && (
                  <div className="flex items-center space-x-2">
                    <img
                      src={URL.createObjectURL(sponsorLogo)}
                      alt="Preview"
                      className="object-cover w-10 h-10 rounded"
                    />
                    <button
                      type="button"
                      onClick={() => setSponsorLogo(null)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Maximum file size: 5MB. Supported formats: PNG, JPG, JPEG
              </p>
            </div>

            <div className="lg:col-span-3">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Sponsor Description
              </label>
              <textarea
                name="sponsor_description"
                value={formData.sponsor_description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              ></textarea>
            </div>
          </div>
        </div>

        {/* Contact Information - Remains same but adjusted for consistency */}
        <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h3 className="mb-6 text-xl font-semibold text-gray-700">
            Contact Information
          </h3>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Contact Name *
              </label>
              <input
                type="text"
                name="sponsor_contact.name"
                value={formData.sponsor_contact.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                type="email"
                name="sponsor_contact.email"
                value={formData.sponsor_contact.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <input
                type="tel"
                name="sponsor_contact.phone"
                value={formData.sponsor_contact.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Updated buttons section */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg
                  className="w-5 h-5 mr-3 -ml-1 text-white animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Submitting...
              </>
            ) : (
              "Submit Application"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SponsorshipApplication;
